import {
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { $$toast } from '@doger/ui/components/ui/toast';
import type { AIEvent } from '@doger/core/shared/api';
import { $$api, ErrorMatcher } from '@doger/core/shared/api';

import { useAuth } from '@doger/chat/modules/auth';
import { useAnalytics } from '@doger/chat/shared/analytics';

import { useTranslation } from '../../../../shared/i18n';
import { ChatCacheManager } from '../chat.cache-manager';
import { CHAT_QUERY_KEYS } from '../chat.query-keys';
import { RunAgentResponseStreamParser } from '../run-agent-stream-response.parser';

const sendMessageToChatArgsSchema = z.object({
  sessionId: z.string(),
  text: z.string(),
  appName: z.string(),
  type: z.enum(['create', 'send']),
  from: z.enum(['text', 'suggestion']),
});

type SendMessageToChatArgs = z.infer<typeof sendMessageToChatArgsSchema>;

export const useSendMessageMutation = () => {
  const queryClient = useQueryClient();
  const { setIsWhitelistModalOpen, setIsEmailConfirmationModalOpen } =
    useAuth();
  const { t } = useTranslation('app');
  const analytics = useSendMessageToChatAnalytics();

  const runAgentMutation = useMutation({
    mutationKey: CHAT_QUERY_KEYS.mutations.sendMessageToChat(),
    mutationFn: async (args: SendMessageToChatArgs) => {
      const STREAMING = false;

      sendMessageToChatArgsSchema.parse(args);

      analytics.track(args);

      const response = await $$api.runAgentSSE({
        sessionId: args.sessionId,
        text: args.text,
        streaming: STREAMING,
      });

      const manager = new ChatCacheManager(queryClient);

      const handler = STREAMING
        ? new AgentResponseStreamHandler(manager)
        : new AgentResponseHandler(manager);

      await new RunAgentResponseStreamParser()
        .onChunk((chunk) => {
          handler.onChunk(args, chunk);
        })
        .onError((error) => {
          console.error('error', error);
        })
        .parse(response);
    },

    onMutate: async (args) => {
      const manager = new ChatCacheManager(queryClient);

      await queryClient.cancelQueries({
        queryKey: CHAT_QUERY_KEYS.queries.session(args.sessionId),
      });

      manager.addUserMessageToSession({
        sessionId: args.sessionId,
        message: args.text,
      });
    },

    onError: (err) => {
      console.error('runAgent failed:', err);

      if (ErrorMatcher.isAppError(err, 'NOT_ON_WHITELIST')) {
        $$toast.error(t('whitelist.toast.pending-application.title'));
      } else if (ErrorMatcher.isAppError(err, 'EMAIL_NOT_CONFIRMED')) {
        $$toast.error(t('auth.emailNotConfirmed'), {
          action: {
            label: t('auth.emailConfirmationReminder.actionLabel', {
              ns: 'core',
            }),
            onClick: () => {
              setIsEmailConfirmationModalOpen(true);
            },
          },
        });
      } else if (ErrorMatcher.isAppError(err, 'COMPLETED_PROFILE_REQUIRED')) {
        $$toast.error(t('whitelist.toast.not-submitted-application.title'), {
          action: {
            label: t('whitelist.toast.not-submitted-application.action'),
            onClick: () => {
              setIsWhitelistModalOpen(true);
            },
          },
        });
      } else {
        $$toast.error(t('errors.chat.failedToSend'));
      }
    },
  });

  return runAgentMutation;
};

export const useSendMessagePending = (args: { sessionId: string }) =>
  useMutationState({
    filters: {
      mutationKey: CHAT_QUERY_KEYS.mutations.sendMessageToChat(),
    },
  })
    .filter((mutation) => {
      const vars = sendMessageToChatArgsSchema.safeParse(mutation.variables);

      if (!vars.success) {
        return false;
      }

      return vars.data.sessionId === args.sessionId;
    })
    .some((mutation) => mutation.status === 'pending');

const useSendMessageToChatAnalytics = () => {
  const analytics = useAnalytics();

  return {
    track: (args: SendMessageToChatArgs) => {
      switch (args.type) {
        case 'create': {
          switch (args.from) {
            case 'text': {
              analytics.amplitude.track({
                event: 'User Created Chat',
                properties: {
                  chatId: args.sessionId,
                  model: args.appName,
                },
              });

              break;
            }

            case 'suggestion': {
              analytics.amplitude.track({
                event: 'User Created Chat From Suggestion',
                properties: {
                  chatId: args.sessionId,
                  suggestion: args.text,
                  model: args.appName,
                },
              });

              break;
            }

            default: {
              const _: never = args.from;

              analytics.amplitude.track({
                event: 'User Created Chat',
                properties: {
                  chatId: args.sessionId,
                  model: args.appName,
                },
              });

              console.error('[useSendMessageToChatAnalytics] Invalid from:', _);

              break;
            }
          }

          break;
        }

        case 'send': {
          analytics.amplitude.track({
            event: 'User Sent Message',
            properties: {
              chatId: args.sessionId,
              model: args.appName,
            },
          });

          break;
        }

        default: {
          const _: never = args.type;

          console.error('[useSendMessageToChatAnalytics] Invalid type:', _);

          analytics.amplitude.track({
            event: 'User Sent Message',
            properties: {
              chatId: args.sessionId,
              model: args.appName,
            },
          });
          break;
        }
      }
    },
  };
};

class AgentResponseHandler {
  public constructor(private readonly manager: ChatCacheManager) {}

  public onChunk(args: { sessionId: string }, chunk: AIEvent) {
    this.manager.addAgentResponseToChat({
      sessionId: args.sessionId,
      event: chunk,
    });
  }
}

class AgentResponseStreamHandler {
  private isNewMessage = true;

  public constructor(private readonly manager: ChatCacheManager) {}

  public onChunk(args: { sessionId: string }, chunk: AIEvent) {
    if (!('partial' in chunk)) {
      this.handleCompleteChunk(args, chunk);

      return;
    }

    if (chunk.partial) {
      if (this.isNewMessage) {
        this.handleFirstPartialChunk(args, chunk);

        return;
      }

      this.handlePartialChunk(args, chunk);

      return;
    }

    this.handleCompleteChunk(args, chunk);
  }

  private handleFirstPartialChunk(args: { sessionId: string }, chunk: AIEvent) {
    this.manager.addAgentResponseToChat({
      sessionId: args.sessionId,
      event: chunk,
    });
    this.isNewMessage = false;
  }

  private handlePartialChunk(args: { sessionId: string }, chunk: AIEvent) {
    this.manager.ammendLastAgentResponseToChat({
      sessionId: args.sessionId,
      text: chunk.content.parts[0].text!,
    });
  }

  private handleCompleteChunk(args: { sessionId: string }, chunk: AIEvent) {
    if (!this.isNewMessage) {
      this.manager.removeLastAgentReponseFromChat({
        sessionId: args.sessionId,
      });
    }
    this.manager.addAgentResponseToChat({
      sessionId: args.sessionId,
      event: chunk,
    });
    this.isNewMessage = true;
  }
}
